import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { api, formatError } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Award, Trophy, Sparkles, ShieldCheck, Loader2, CheckCircle2, XCircle,
  ChevronLeft, ChevronRight, RotateCcw, Lock, GraduationCap, ArrowRight,
  ClipboardList, Target, User, BookOpen, Flag, Quote, Clock, Infinity,
  BadgeCheck, AlertTriangle, Info, Check,
} from "lucide-react";

export default function FinalExam() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [ack, setAck] = useState(false);
  const [phase, setPhase] = useState("landing"); // landing → preamble → quiz → result
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [locking, setLocking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const nav = useNavigate();

  // Admin tour-picker override: when the Walkthroughs editor opens this
  // page via `?_tourPicker=<stepId>`, the admin needs to target the
  // pre-exam landing elements (`final-landing-hero`, `final-bottom-line`,
  // `final-take-exam-cta`). For an admin who's already passed, the
  // normal flow renders the "Final exam complete" takeover and none of
  // those selectors exist. Detecting the query param lets the picker
  // see the live landing regardless of exam state.
  const isPickerPreview = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).has("_tourPicker");
    } catch { return false; }
  }, []);

  useEffect(() => {
    api.get("/exam/final").then((r) => {
      setData(r.data);
      const remaining = r.data?.retake?.cooldown_remaining_seconds || 0;
      if (remaining > 0) setCooldown(remaining);
    }).catch((e) => setError(formatError(e)));
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((n) => (n > 0 ? n - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const total = data?.questions?.length || 0;
  const q = data?.questions?.[current];
  const chosen = answers[current];
  const isLast = current === total - 1;
  const pct = total ? ((current + (chosen ? 1 : 0)) / total) * 100 : 0;

  const pickAnswer = (letter) => {
    if (locking) return;
    setAnswers((prev) => ({ ...prev, [current]: letter }));
    setLocking(true);
    setTimeout(() => {
      setLocking(false);
      if (isLast) submitFull({ ...answers, [current]: letter });
      else setCurrent((c) => Math.min(c + 1, total - 1));
    }, 500);
  };

  const submitFull = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const payload = {
        answers: data.questions.map((_, i) => finalAnswers[i] || ""),
        acknowledged_code: true,
        attempt_token: data.attempt_token,
      };
      const { data: res } = await api.post("/exam/final/submit", payload);
      setResult(res);
      setPhase("result");
      const remaining = res?.retake?.cooldown_remaining_seconds || 0;
      setCooldown(remaining);
      if (res.passed) toast.success(`Passed! ${res.correct}/${res.total} correct`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && detail?.cooldown_remaining_seconds) {
        setCooldown(detail.cooldown_remaining_seconds);
        toast.error(detail.message || "Cool-down active — please wait before retaking.");
      } else {
        toast.error(formatError(err));
      }
    } finally { setSubmitting(false); }
  };

  const retake = async () => {
    setAnswers({}); setResult(null); setCurrent(0); setPhase("landing"); setAck(false);
    try {
      const r = await api.get("/exam/final");
      setData(r.data);
      const remaining = r.data?.retake?.cooldown_remaining_seconds || 0;
      setCooldown(remaining);
    } catch { /* if fetch fails the submit will surface 429 */ }
  };

  if (error) {
    return <AppShell title="Final Certification Exam">
      <div className="surface p-10 max-w-xl">
        <p className="font-display text-2xl">Exam unavailable</p>
        <p className="text-sm text-muted-foreground mt-2">{error}</p>
      </div>
    </AppShell>;
  }

  if (!data) {
    return <AppShell title="Final Certification Exam">
      <div className="surface p-10 max-w-xl flex items-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading final exam…
      </div>
    </AppShell>;
  }

  // ─────────────────────────────────────── Completed state (already passed)
  // Suppressed while the admin walkthroughs picker is active so the
  // editor can target the live pre-exam landing elements.
  if (data.completed && !isPickerPreview) {
    return (
      <AppShell title="Final Certification Exam">
        <div className="w-full">
          <div className="rounded-3xl p-8 md:p-10 bg-gradient-to-br from-primary via-fuchsia-600 to-indigo-600 text-white relative overflow-hidden" data-testid="final-completed">
            <Trophy className="absolute right-8 top-1/2 -translate-y-1/2 w-64 h-64 text-white/10" strokeWidth={1} />
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl grid place-items-center bg-white/15 backdrop-blur">
                <Trophy className="w-7 h-7" />
              </div>
              <p className="mt-4 text-[11px] uppercase tracking-widest font-semibold text-white/85">
                Certified CPG Founder™ — Awarded
              </p>
              <h2 className="font-display text-4xl md:text-5xl mt-1">Final exam complete</h2>
              <p className="mt-3 text-sm md:text-base max-w-2xl text-white/90">
                You've earned the Certified CPG Founder™ credential
                {data.best_score ? ` with a best score of ${data.best_score}%` : ""}.
                {data.credential_id
                  ? <> Credential ID: <span className="font-mono">{data.credential_id}</span>.</>
                  : null}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={() => nav("/progress")}
                  className="bg-white text-primary rounded-full px-5 py-2.5 text-xs font-semibold hover:shadow-lg transition"
                  data-testid="final-completed-view-cert">
                  <Award className="w-4 h-4 inline mr-1" /> View my certificate
                </button>
              </div>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  // ─────────────────────────────────────── Result screen
  if (phase === "result" && result) {
    const pass = result.passed;
    return (
      <AppShell title="Final Certification Exam">
        <div className="w-full">
          <div className={[
            "rounded-2xl p-8 md:p-10",
            pass ? "grad-band text-white" : "border-2 border-pink-300 bg-pink-50 text-pink-950",
          ].join(" ")} data-testid={pass ? "final-pass" : "final-fail"}>
            <div className={["w-14 h-14 rounded-2xl grid place-items-center",
              pass ? "bg-white/20" : "bg-pink-100 text-pink-700"].join(" ")}>
              {pass ? <Trophy className="w-7 h-7" /> : <RotateCcw className="w-7 h-7" />}
            </div>
            <p className={["mt-4 text-[10px] uppercase tracking-widest font-semibold",
              pass ? "text-white/80" : "text-pink-700"].join(" ")}>
              {pass ? "Certified CPG Founder™ — Awarded" : "Not yet — you need 80% to pass"}
            </p>
            <h2 className="font-display text-4xl mt-1">
              {result.correct}<span className={pass ? "text-white/70" : "text-pink-700"}>/{result.total}</span>
              <span className={["ml-4 text-2xl align-middle", pass ? "text-white/80" : "text-pink-700"].join(" ")}>
                {result.score_pct}%
              </span>
            </h2>
            <p className={["mt-3 text-sm md:text-base max-w-xl", pass ? "text-white/90" : "text-pink-900"].join(" ")}>
              {pass
                ? `Congratulations. Your primary credential has been issued. Credential ID: ${result.credential_id}. Log into the Progress page to download your certificate.`
                : cooldown > 0
                ? `Review the misses below. You can retake in ${formatCooldown(cooldown)} with a fresh, randomised set of questions.`
                : `Review the misses below and retake — you'll see a fresh randomised set of questions.`}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              {pass ? (
                <button onClick={() => nav("/progress")} className="bg-white text-primary rounded-full px-5 py-2.5 text-xs font-semibold" data-testid="final-view-cert">
                  <Award className="w-4 h-4 inline mr-1" /> Download my certificate
                </button>
              ) : (
                <button onClick={retake} disabled={cooldown > 0}
                  className="bg-pink-800 text-white rounded-full px-5 py-2.5 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="final-retake">
                  <RotateCcw className="w-3.5 h-3.5" />
                  {cooldown > 0 ? `Wait ${formatCooldown(cooldown)} before retaking` : "Retake exam"}
                </button>
              )}
            </div>
          </div>

          <div className="mt-6 grid gap-2" data-testid="final-review">
            {result.results.map((r, i) => (
              <div key={i} className="surface px-4 py-3 flex items-start gap-3 text-sm">
                {r.correct ? <CheckCircle2 className="w-4 h-4 text-green-600 mt-1 shrink-0" />
                            : <XCircle className="w-4 h-4 text-red-500 mt-1 shrink-0" />}
                <div className="flex-1">
                  <p className="font-semibold">Q{i + 1}: {data.questions[i].q}</p>
                  {!r.correct && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Correct answer: <span className="font-semibold text-foreground">{r.correct_answer}</span>
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </AppShell>
    );
  }

  // ─────────────────────────────────────── Preamble / Code of Excellence
  if (phase === "preamble") {
    return (
      <AppShell title="Final Certification Exam">
        <div className="w-full">
          <div className="surface p-7" data-testid="final-preamble">
            <p className="text-xs uppercase tracking-widest text-primary font-semibold flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> CPG Founder Code of Excellence
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Every certified founder commits to these standards. Please read carefully and acknowledge
              before starting the final exam.
            </p>
            <ul className="mt-5 grid gap-3">
              {data.code_of_excellence.map((line, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full grid place-items-center text-xs font-bold shrink-0 bg-gradient-to-br from-primary to-shell-accent text-white mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-sm leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>

            <label className="mt-6 flex items-start gap-3 cursor-pointer p-4 rounded-xl bg-muted/40 border border-shell-border">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                data-testid="final-ack"
                className="mt-1 w-4 h-4 accent-primary" />
              <span className="text-sm leading-relaxed">
                I acknowledge the CPG Founder Code of Excellence and agree to uphold it as a Certified CPG Founder™.
              </span>
            </label>

            <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
              <div className="text-xs text-muted-foreground">
                {total} questions · 80% to pass · No penalty for retakes
              </div>
              <button onClick={() => setPhase("quiz")} disabled={!ack} data-testid="final-start"
                className="btn-primary disabled:opacity-60">
                <Sparkles className="w-4 h-4" /> Start final exam
              </button>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  // ─────────────────────────────────────── Quiz phase — one question at a time
  if (phase === "quiz") {
    const isTF = q.type === "TF";
    const letters = isTF ? ["True", "False"] : ["A", "B", "C", "D"].slice(0, q.options.length);
    return (
      <AppShell title="Final Certification Exam">
        <div className="w-full">
          <div className="mb-4 flex items-center justify-between text-xs uppercase tracking-widest font-semibold text-muted-foreground">
            <span>Question <span className="text-foreground">{current + 1}</span> of {total}</span>
            <span>{Math.round(pct)}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden mb-6">
            <div className="h-full bg-gradient-to-r from-primary to-shell-accent transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>

          <div key={current} className="surface p-7 md:p-9 animate-in fade-in slide-in-from-right-3 duration-300" data-testid={`final-q-${current}`}>
            <p className="text-[10px] uppercase tracking-widest text-primary font-bold">
              {isTF ? "True / False" : q.type === "SC" ? "Scenario" : "Multiple choice"}
            </p>
            <h3 className="font-display text-2xl leading-snug mt-2">{q.q}</h3>
            <div className="mt-6 grid gap-3">
              {q.options.map((opt, i) => {
                const letter = letters[i];
                const isChosen = chosen === letter;
                return (
                  <button key={i} type="button" disabled={locking && !isChosen} onClick={() => pickAnswer(letter)}
                    data-testid={`final-q-${current}-opt-${letter}`}
                    className={["w-full text-left rounded-2xl border px-4 py-3.5 text-sm flex items-start gap-3 transition-all duration-200",
                      isChosen ? "border-primary bg-primary/10 shadow-sm scale-[1.01]"
                      : locking ? "border-shell-border bg-white/60 opacity-50"
                      : "border-shell-border bg-white hover:border-primary hover:-translate-y-0.5"].join(" ")}>
                    <span className={["w-8 h-8 rounded-full grid place-items-center text-xs font-bold shrink-0",
                      isChosen ? "bg-primary text-white" : "bg-muted text-foreground"].join(" ")}>
                      {isTF ? letter[0] : letter}
                    </span>
                    <span className="flex-1 pt-1">{opt}</span>
                    {isChosen && <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-1" />}
                  </button>
                );
              })}
            </div>
            <div className="mt-6 flex items-center justify-between text-xs">
              <button onClick={() => setCurrent((c) => Math.max(0, c - 1))} disabled={current === 0 || locking || submitting}
                className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground disabled:opacity-40" data-testid="final-prev">
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </button>
              {submitting ? (
                <span className="inline-flex items-center gap-1 text-primary font-semibold">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Scoring…
                </span>
              ) : locking ? (
                <span className="inline-flex items-center gap-1 text-primary font-semibold">
                  {isLast ? "Submitting…" : "Locking in…"} <ChevronRight className="w-3.5 h-3.5" />
                </span>
              ) : (
                <span className="text-muted-foreground">
                  {isLast ? "Pick an answer to finish" : "Pick an answer to continue"}
                </span>
              )}
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  // ─────────────────────────────────────── Landing (three tiered states)
  return (
    <AppShell title="Final Certification Exam">
      <FinalExamLanding
        data={data}
        onStart={() => setPhase("preamble")}
        onGoTo={nav}
      />
    </AppShell>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Landing page — shared shell for all 3 gate states.
// Only the top-right status card + module-status chips + optional
// "What You Get" panel change per state. Everything else is fixed.
// ═══════════════════════════════════════════════════════════════════
function FinalExamLanding({ data, onStart, onGoTo }) {
  const gate = data.gate || { state: "ready", is_paid: true, core_modules: [], core_total: 10, completed_count: 0 };
  const state = gate.state; // "ready" | "locked_free" | "locked_incomplete"
  const coreModules = gate.core_modules || [];
  const coreTotal = gate.core_total || coreModules.length || 10;
  const completedCount = gate.completed_count ?? coreModules.filter(m => m.status === "passed").length;

  return (
    <div className="w-full">
      {/* ── Hero band with variable right-side status card ─────────── */}
      <div
        className="rounded-3xl p-8 md:p-10 relative overflow-hidden text-white"
        style={{ background: "linear-gradient(115deg, #E4368C 0%, #B33FB6 45%, #6A4CD9 100%)" }}
        data-testid="final-landing-hero"
      >
        {/* Trophy watermark — placed further right so it sits behind
            the variable status card, matching the reference designs. */}
        <Trophy className="absolute right-[22rem] lg:right-[19rem] top-1/2 -translate-y-1/2 w-80 h-80 text-white/10 hidden xl:block pointer-events-none" strokeWidth={1.2} />

        <div className="relative grid xl:grid-cols-[1fr_360px] gap-8 items-start">
          <div>
            <div className="w-14 h-14 rounded-2xl grid place-items-center bg-white/15 backdrop-blur">
              <Trophy className="w-7 h-7" />
            </div>
            <p className="mt-5 text-[11px] uppercase tracking-[0.18em] font-semibold text-white/85">
              Final Certification Exam
            </p>
            <h1 className="font-display text-4xl md:text-5xl mt-1 leading-tight">
              Certified CPG Founder<span className="align-super text-xl">™</span>
            </h1>
            <p className="mt-4 text-sm md:text-base text-white/90 max-w-2xl leading-relaxed">
              The capstone assessment that validates your mastery across the entire
              CPG operating system — from entity formation to retail velocity to profit math.
            </p>

            <div className="mt-8 grid grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
              <StatPill icon={Clock} value="50" label="Questions" />
              <StatPill icon={BadgeCheck} value="80%" label="Minimum to Pass" sub="(40+ correct)" />
              <StatPill icon={Infinity} value="Unlimited" label="Retakes" />
              <StatPill icon={Award} value="Earn" label="Your Credential" />
            </div>
          </div>

          {/* Variable right-side status card */}
          <StatusPanel state={state} onStart={onStart} onGoTo={onGoTo} />
        </div>
      </div>

      {/* ── Three-column info: Format / What It Tests / Eligibility ── */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <InfoCard icon={ClipboardList} accent="primary" title="Format & Requirements">
          <BulletRow>50 questions — multi-domain, cumulative, practical scenarios</BulletRow>
          <BulletRow>80% minimum score required to pass (40+ correct answers)</BulletRow>
          <BulletRow>Unlimited retakes — you can retake until you hit 80%</BulletRow>
          <BulletRow>Earns the Certified CPG Founder™ credential upon passing</BulletRow>
        </InfoCard>

        <InfoCard icon={Target} accent="indigo" title="What It Tests">
          <p className="text-sm text-foreground/80 mb-3 leading-relaxed">
            The Final Exam pulls from all <span className="font-semibold">10 core modules</span> and tests your ability to:
          </p>
          <BulletRow accent="indigo">Navigate cross-functional CPG decisions</BulletRow>
          <BulletRow accent="indigo">
            Interpret <span className="font-semibold">real scenarios</span> (retail velocity, MOQs, freight, margin compression)
          </BulletRow>
          <BulletRow accent="indigo">
            Identify <span className="font-semibold">red flags</span> (bad contracts, non-compliant labels, working capital traps)
          </BulletRow>
          <BulletRow accent="indigo">
            Apply <span className="font-semibold">financial discipline</span> (margin math, COGS, break-even, SKU profitability)
          </BulletRow>
          <p className="text-xs text-muted-foreground mt-3 italic leading-relaxed">
            It's designed to reflect the real operating environment you'll face as a CPG founder.
          </p>
        </InfoCard>

        <InfoCard icon={User} accent="green" title="Eligibility">
          <p className="text-sm text-foreground/80 leading-relaxed">
            You must complete <span className="font-semibold">all 10 core modules</span> and pass
            <span className="font-semibold"> each quiz at 80% or higher.</span>
          </p>
          <div className="mt-4 grid place-items-center">
            <EligibilityRing completed={completedCount} total={coreTotal} state={state} />
          </div>
          <EligibilityBanner
            completed={completedCount}
            total={coreTotal}
            state={state}
          />
        </InfoCard>
      </div>

      {/* ── Modules grid + Next Steps (+ optional What You Get) ────── */}
      <div className={[
        "mt-6 grid gap-4",
        state === "locked_free" ? "lg:grid-cols-[1fr_1fr_1fr]" : "lg:grid-cols-[1.4fr_1fr]",
      ].join(" ")}>
        <ModulesGrid modules={coreModules} showStatus={state === "locked_incomplete"} state={state} />

        {state === "locked_free" && <WhatYouGetCard />}

        <NextStepsCard state={state} />
      </div>

      {/* ── Bottom line callout ─────────────────────────────────── */}
      <div
        className="mt-6 rounded-2xl border border-primary/15 bg-gradient-to-br from-primary/5 via-white to-fuchsia-50 p-5 md:p-6 flex items-start gap-4"
        data-testid="final-bottom-line"
      >
        <div className="w-10 h-10 rounded-xl grid place-items-center bg-primary/10 text-primary shrink-0">
          <Quote className="w-5 h-5" />
        </div>
        <div className="text-sm leading-relaxed">
          <p className="font-semibold text-primary">Bottom line:</p>
          <p className="text-foreground/80 mt-1">
            The Final Exam is the proof point that you've built the full-stack operating fluency
            required to launch, scale, and defend margin in the CPG arena.
          </p>
          <p className="mt-1 font-semibold text-foreground">
            It's not gatekeeping — it's validation that you've done the work.
          </p>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Variable right-side status card (the ONLY thing that changes)
// ═══════════════════════════════════════════════════════════════════
function StatusPanel({ state, onStart, onGoTo }) {
  const base = "rounded-2xl p-6 bg-[#0F1B36]/85 backdrop-blur border border-white/10 shadow-xl";

  if (state === "ready") {
    return (
      <div className={base} data-testid="final-status-ready">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full grid place-items-center bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30">
            <GraduationCap className="w-5 h-5" />
          </span>
          <p className="font-display text-lg text-emerald-300">You're eligible!</p>
        </div>
        <p className="mt-3 text-sm text-white/85 leading-relaxed">
          You've completed all 10 core modules and passed each quiz at <span className="font-semibold text-white">80% or higher.</span>
        </p>
        <button
          onClick={onStart}
          className="mt-5 w-full rounded-full bg-white text-primary font-semibold py-3 text-sm hover:shadow-lg hover:shadow-primary/30 transition"
          data-testid="final-take-exam-cta"
        >
          Take the Final Exam
        </button>
        <p className="mt-3 text-[11px] text-white/60 text-center leading-snug">
          Start the exam when you're ready.<br />Unlimited attempts.
        </p>
      </div>
    );
  }

  if (state === "locked_free") {
    return (
      <div className={base} data-testid="final-status-locked-free">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full grid place-items-center bg-primary/15 text-primary ring-1 ring-primary/30">
            <Lock className="w-5 h-5" />
          </span>
          <p className="font-display text-lg text-white">Unlock the Final Exam</p>
        </div>
        <p className="mt-3 text-sm text-white/85 leading-relaxed">
          The Final Exam is available for <span className="font-semibold text-white">Pro members</span>.
        </p>
        <p className="mt-2 text-sm text-white/85 leading-relaxed">
          Upgrade your account to take the Final Exam and earn your Certified CPG Founder™ credential.
        </p>
        <button
          onClick={() => onGoTo("/account/subscription")}
          className="mt-5 w-full rounded-full bg-gradient-to-r from-primary to-fuchsia-500 text-white font-semibold py-3 text-sm hover:shadow-lg hover:shadow-primary/30 transition"
          data-testid="final-upgrade-cta"
        >
          Upgrade to Pro
        </button>
        <p className="mt-3 text-[11px] text-white/60 text-center">Cancel anytime.</p>
      </div>
    );
  }

  // locked_incomplete
  return (
    <div className={base} data-testid="final-status-locked-incomplete">
      <div className="flex items-center gap-2.5">
        <span className="w-9 h-9 rounded-full grid place-items-center bg-indigo-500/15 text-indigo-300 ring-1 ring-indigo-400/30">
          <Lock className="w-5 h-5" />
        </span>
        <p className="font-display text-lg text-white">Complete Your Core Modules</p>
      </div>
      <p className="mt-3 text-sm text-white/85 leading-relaxed">
        You're a <span className="font-semibold text-white">Pro member</span>, but you need to complete
        all 10 core modules and pass each module quiz at 80% or higher before you can take the Final Exam.
      </p>
      <button
        onClick={() => onGoTo("/course")}
        className="mt-5 w-full rounded-full bg-white text-primary font-semibold py-3 text-sm hover:shadow-lg transition"
        data-testid="final-continue-modules-cta"
      >
        Continue Core Modules
      </button>
      <p className="mt-3 text-[11px] text-white/60 text-center leading-snug">
        Complete all requirements<br />to unlock your exam.
      </p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Small building blocks
// ═══════════════════════════════════════════════════════════════════
function StatPill({ icon: Icon, value, label, sub }) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      <span className="w-10 h-10 rounded-xl grid place-items-center bg-white/10 backdrop-blur ring-1 ring-white/15 shrink-0">
        <Icon className="w-5 h-5" />
      </span>
      <div className="min-w-0">
        <p className="font-display text-lg leading-none">{value}</p>
        <p className="text-[11px] text-white/80 mt-0.5">{label}{sub ? <><br /><span className="text-white/60">{sub}</span></> : null}</p>
      </div>
    </div>
  );
}

const ACCENT = {
  primary: {
    bg: "bg-primary/10", ring: "ring-primary/20", text: "text-primary",
    dot: "bg-primary text-white",
  },
  indigo:  {
    bg: "bg-indigo-500/10", ring: "ring-indigo-500/20", text: "text-indigo-600",
    dot: "bg-indigo-500 text-white",
  },
  green:   {
    bg: "bg-emerald-500/10", ring: "ring-emerald-500/20", text: "text-emerald-600",
    dot: "bg-emerald-500 text-white",
  },
};

function InfoCard({ icon: Icon, title, accent = "primary", children }) {
  const a = ACCENT[accent];
  return (
    <div className="rounded-2xl border border-shell-border bg-white p-5 md:p-6 shadow-sm">
      <div className="flex items-center gap-2.5 mb-3.5">
        <span className={["w-10 h-10 rounded-xl grid place-items-center ring-1", a.bg, a.ring, a.text].join(" ")}>
          <Icon className="w-5 h-5" />
        </span>
        <h3 className="font-display text-lg">{title}</h3>
      </div>
      <div>{children}</div>
    </div>
  );
}

function BulletRow({ children, accent = "primary" }) {
  const a = ACCENT[accent];
  return (
    <div className="flex items-start gap-2.5 py-1.5">
      <span className={["w-4 h-4 rounded-full grid place-items-center mt-0.5 shrink-0", a.dot].join(" ")}>
        <Check className="w-2.5 h-2.5" strokeWidth={3} />
      </span>
      <span className="text-sm text-foreground/85 leading-relaxed">{children}</span>
    </div>
  );
}

function EligibilityRing({ completed, total, state }) {
  const denom = total > 0 ? total : 10;
  const shown = Math.min(denom, Math.max(0, completed));
  const pct = denom > 0 ? Math.min(100, Math.round((shown / denom) * 100)) : 0;
  const isFull = shown >= denom;
  const ringColor = isFull ? "#10B981" : "#7C3AED";
  const bg = isFull ? "#D1FAE5" : "#EDE9FE";
  const style = {
    background: `conic-gradient(${ringColor} 0deg ${pct * 3.6}deg, ${bg} ${pct * 3.6}deg 360deg)`,
  };
  return (
    <div className="relative w-32 h-32 rounded-full grid place-items-center" style={style} data-testid="final-eligibility-ring">
      <div className="w-24 h-24 rounded-full bg-white grid place-items-center text-center">
        <div>
          <p className="font-display text-2xl leading-none">
            {shown}<span className="text-muted-foreground">/{denom}</span>
          </p>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Core Modules<br />Completed</p>
        </div>
      </div>
    </div>
  );
}

function EligibilityBanner({ completed, total, state }) {
  const denom = total > 0 ? total : 10;
  const remaining = Math.max(0, denom - completed);
  if (state === "ready" || remaining === 0) {
    return (
      <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3.5 flex items-start gap-2.5" data-testid="final-eligibility-banner-success">
        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
        <div className="text-xs leading-relaxed">
          <p className="font-semibold text-emerald-800">All core modules completed!</p>
          <p className="text-emerald-900/90 mt-0.5">You're eligible to take the Final Exam.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="mt-4 rounded-xl border border-pink-200 bg-pink-50 p-3.5 flex items-start gap-2.5" data-testid="final-eligibility-banner-warning">
      <AlertTriangle className="w-4 h-4 text-pink-600 shrink-0 mt-0.5" />
      <div className="text-xs leading-relaxed">
        <p className="font-semibold text-pink-800">{remaining} module{remaining === 1 ? "" : "s"} remaining</p>
        <p className="text-pink-900/90 mt-0.5">
          Complete and pass each remaining module quiz at 80%+ to unlock the Final Exam.
        </p>
      </div>
    </div>
  );
}

const CORE_FALLBACK = [
  { slug: "m1",  name: "Company & Legal Setup" },
  { slug: "m2",  name: "Product Development & Formulation" },
  { slug: "m3",  name: "Branding & Positioning" },
  { slug: "m4",  name: "Packaging & Label Compliance" },
  { slug: "m5",  name: "Website & Digital Presence" },
  { slug: "m6",  name: "Social Media & Brand Growth" },
  { slug: "m7",  name: "Retail Sales & Distribution" },
  { slug: "m8",  name: "Manufacturing & Supply Chain" },
  { slug: "m9",  name: "Logistics & 3PL" },
  { slug: "m10", name: "Accounting & Finance" },
];

function ModulesGrid({ modules, showStatus, state }) {
  const list = (modules && modules.length ? modules : CORE_FALLBACK).slice(0, 10);
  return (
    <div className="rounded-2xl border border-shell-border bg-white p-5 md:p-6 shadow-sm" data-testid="final-modules-grid">
      <div className="flex items-center gap-2.5 mb-4">
        <span className="w-10 h-10 rounded-xl grid place-items-center bg-primary/10 ring-1 ring-primary/20 text-primary shrink-0">
          <BookOpen className="w-5 h-5" />
        </span>
        <h3 className="font-display text-lg">
          <span className="text-primary">10 Core Modules</span>
          <span className="text-foreground"> — All Must Be Passed at 80%+</span>
        </h3>
      </div>

      {showStatus ? (
        <div className="grid gap-2">
          {list.map((m, i) => (
            <div key={m.slug} className="flex items-center gap-3 py-1.5 min-w-0" data-testid={`final-module-row-${m.slug}`}>
              <NumberBubble n={i + 1} />
              <span className="text-sm text-foreground flex-1 min-w-0 truncate">{cleanModuleName(m.name, i + 1)}</span>
              <span className="shrink-0"><StatusChip status={m.status} /></span>
            </div>
          ))}
          <div className="mt-3 rounded-xl border border-sky-100 bg-sky-50/70 p-3 flex items-start gap-2.5">
            <Info className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
            <p className="text-xs text-sky-900 leading-relaxed">
              Each module quiz must be passed at 80% or higher to count toward eligibility.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2.5">
            {list.map((m, i) => (
              <div key={m.slug} className="flex items-start gap-3" data-testid={`final-module-row-${m.slug}`}>
                <NumberBubble n={i + 1} />
                <span className="text-sm text-foreground/90 leading-snug pt-0.5">{cleanModuleName(m.name, i + 1)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-xl border border-sky-100 bg-sky-50/70 p-3 flex items-start gap-2.5">
            <Info className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
            <p className="text-xs text-sky-900 leading-relaxed">
              Watching videos or reading lessons alone doesn't count — each module quiz
              <span className="font-semibold"> must be passed at 80%+</span> to unlock progress and access the Final Exam.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function cleanModuleName(name, n) {
  // Strip a leading "Module X:" prefix if present so the numbered bubble
  // isn't duplicated in the row text.
  if (!name) return `Module ${n}`;
  return name.replace(/^\s*Module\s*\d+\s*[:\-]\s*/i, "").trim() || name;
}

function NumberBubble({ n }) {
  return (
    <span className="w-6 h-6 rounded-full grid place-items-center text-[11px] font-bold bg-primary/10 text-primary shrink-0">
      {n}
    </span>
  );
}

function StatusChip({ status }) {
  const base = "text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap";
  if (status === "passed") {
    return <span className={`${base} bg-emerald-100 text-emerald-700`} data-testid="final-chip-passed">Passed</span>;
  }
  if (status === "in_progress") {
    return <span className={`${base} bg-pink-100 text-pink-700`} data-testid="final-chip-in-progress">In Progress</span>;
  }
  return <span className={`${base} bg-slate-100 text-slate-500`} data-testid="final-chip-not-started">Not Started</span>;
}

function WhatYouGetCard() {
  return (
    <div className="rounded-2xl border border-pink-200 bg-pink-50/40 p-5 md:p-6 shadow-sm" data-testid="final-what-you-get">
      <div className="flex items-center gap-2.5 mb-3">
        <span className="w-10 h-10 rounded-xl grid place-items-center bg-pink-100 ring-1 ring-pink-200 text-pink-600 shrink-0">
          <Trophy className="w-5 h-5" />
        </span>
        <h3 className="font-display text-lg">What You Get</h3>
      </div>
      <div className="grid grid-cols-1 gap-4">
        <div>
          <p className="text-sm font-semibold text-pink-900">Certified CPG Founder™ credential</p>
          <p className="text-xs text-pink-900/80 mt-0.5">Shareable proof of CPG fluency</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-pink-900">Graduation from CPG Institute</p>
          <p className="text-xs text-pink-900/80 mt-0.5">Full curriculum completion</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-pink-900">Buyers, brokers, investors</p>
          <p className="text-xs text-pink-900/80 mt-0.5">Know the certification signals readiness</p>
        </div>
      </div>
    </div>
  );
}

function NextStepsCard({ state }) {
  const steps = (
    state === "locked_free" ? [
      "Upgrade to Pro to unlock the Final Exam",
      "Take the Final Exam when it unlocks",
      "Retake if needed — no penalty, unlimited attempts",
      "Earn your Certified CPG Founder™ credential and launch with confidence",
    ]
    : state === "locked_incomplete" ? [
      "Complete the remaining core modules",
      "Pass each module quiz at 80%+",
      "Take the Final Exam when it unlocks",
      "Earn your Certified CPG Founder™ credential",
    ]
    : [
      "Complete all 10 core module quizzes at 80%+ (if you haven't already)",
      "Take the Final Exam when it unlocks",
      "Retake if needed — no penalty, unlimited attempts",
      "Earn your Certified CPG Founder™ credential and launch with confidence",
    ]
  );

  return (
    <div className="rounded-2xl border border-shell-border bg-white p-5 md:p-6 shadow-sm relative overflow-hidden" data-testid="final-next-steps">
      <div className="flex items-center gap-2.5 mb-3">
        <span className="w-10 h-10 rounded-xl grid place-items-center bg-primary/10 ring-1 ring-primary/20 text-primary shrink-0">
          <Flag className="w-5 h-5" />
        </span>
        <h3 className="font-display text-lg">Next Steps</h3>
      </div>
      <ol className="grid gap-2.5">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full grid place-items-center text-[11px] font-bold bg-primary/10 text-primary shrink-0">
              {i + 1}
            </span>
            <span className="text-sm text-foreground/85 leading-relaxed">{s}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function formatCooldown(seconds) {
  if (!seconds || seconds <= 0) return "";
  const s = Math.max(0, Math.floor(seconds));
  const unit = (n, singular) => `${n} ${singular}${n === 1 ? "" : "s"}`;
  if (s < 60) return unit(s, "second");
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) {
    return rs ? `${unit(m, "minute")} ${unit(rs, "second")}` : unit(m, "minute");
  }
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${unit(h, "hour")} ${unit(rm, "minute")}` : unit(h, "hour");
}
